import { useSelector } from '@legendapp/state/react';
import type { BackendTypes } from '@tf/api';
import { createModal, Select, useModalControl } from '@tf/ui';
import { S, type SubmitHandler } from '@tf/utils';
import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { AppFormBuilder, ModalFooter } from '@/components/shared';
import { useCreateAccountMutation } from '@/core/api/account';
import { type ProfileGroups, useProfileGroupsSchemas } from '@/core/api/profiles';
import { useProcessingScripts } from '@/core/hooks';
import { appStore } from '@/core/stores';

interface Props {
	onSuccess: (res: { connectedEntityId: BackendTypes.GraphId }) => void;
	onError: (error: Error) => void;
}

export const AddProfileModal = createModal(({ onSuccess, onError }: Props) => {
	const { onClose } = useModalControl();
	const { data: profileGroups } = useProfileGroupsSchemas();
	const location = useLocation();
	const { groupLabel } = useParams<{ groupLabel: string }>();

	const defs = useSelector(() => appStore.defs.get());

	const getFilteredAccountKinds = () => {
		const allAccountKinds = (defs.options.AccountKind || []).sort((a, b) => a.value.localeCompare(b.value));

		if (!profileGroups?.groups) {
			return allAccountKinds;
		}

		let targetKinds: string[] = [];

		if (location.pathname === '/') {
			const firstGroup = profileGroups.groups[0];
			targetKinds = firstGroup.kinds;
		} else if (groupLabel) {
			const currentGroup = profileGroups.groups.find(
				(group: ProfileGroups) => group.label.toLowerCase() === groupLabel.toLowerCase(),
			);
			if (currentGroup) {
				targetKinds = currentGroup.kinds;
			}
		}

		if (targetKinds.length > 0) {
			return allAccountKinds.filter((kind) => targetKinds.includes(kind.value));
		}

		return allAccountKinds;
	};

	const accountKinds = getFilteredAccountKinds();
	const [selectedAccountKind, setSelectedAccountKind] = useState<string | null>(accountKinds[0]?.value ?? null);

	const getApplicantKinds = (accountKind: string | null): string[] => {
		if (!accountKind) {
			return [];
		}
		const applicantConn = defs.entities[accountKind]?.possibleConnections.find((c) => c.kind === 'APPLICANT');
		return applicantConn?.fromEntities.map((e) => e.kind) ?? [];
	};

	const availableApplicantKinds = getApplicantKinds(selectedAccountKind);

	const [selectedApplicantKind, setSelectedApplicantKind] = useState<string | null>(availableApplicantKinds[0] ?? null);

	const effectiveApplicantKind =
		selectedApplicantKind && availableApplicantKinds.includes(selectedApplicantKind)
			? selectedApplicantKind
			: (availableApplicantKinds[0] ?? null);

	const handleAccountKindChange = (value: string | null) => {
		setSelectedAccountKind(value);
		const nextApplicantKinds = getApplicantKinds(value);
		setSelectedApplicantKind(nextApplicantKinds[0] ?? null);
	};

	const selfSegments =
		selectedAccountKind && effectiveApplicantKind
			? {
					account: defs.selfSegments[selectedAccountKind],
					entity: defs.selfSegments[effectiveApplicantKind],
				}
			: null;

	const scripts = useProcessingScripts(selfSegments?.entity ? [selfSegments.entity] : []);

	const createAccountMutation = useCreateAccountMutation();
	const submitHandler: SubmitHandler = async ({ values }) => {
		if (!selectedAccountKind || !selfSegments?.entity || !effectiveApplicantKind) {
			onError(new Error('Missing account or applicant kind'));
			return;
		}

		const requiredConnections: BackendTypes.RequiredConnectionSelection[] | undefined =
			availableApplicantKinds.length > 1
				? [{ connectionKind: 'APPLICANT', entityKind: effectiveApplicantKind }]
				: undefined;

		createAccountMutation.mutate(
			{
				accountKind: selectedAccountKind,
				entitySelfSegmentKind: selfSegments.entity,
				entitySelfSegmentData: values[selfSegments.entity],
				requiredConnections,
			},
			{
				onSuccess,
				onError,
			},
		);
	};

	return (
		<>
			<Select
				m="0.75rem"
				label="Type"
				placeholder="Select a type"
				data={accountKinds}
				data-autofocus
				value={selectedAccountKind}
				onChange={handleAccountKindChange}
			/>

			{availableApplicantKinds.length > 1 && (
				<Select
					m="0 0.75rem 0.75rem"
					label="Applicant type"
					placeholder="Select applicant entity type"
					data={availableApplicantKinds.map((kind) => ({
						value: kind,
						label: S.prettify(kind),
					}))}
					value={effectiveApplicantKind}
					onChange={setSelectedApplicantKind}
				/>
			)}

			{selfSegments?.entity && effectiveApplicantKind && (
				<AppFormBuilder
					key={effectiveApplicantKind}
					segments={[{ segmentKind: selfSegments.entity, graphId: 0, accessMode: 'MODIFY' }]}
					processingScripts={scripts}
					isLoading={createAccountMutation.isPending}
					onSubmit={submitHandler}
					styles={{ content: { padding: '0 .75rem .25rem' } }}
					shouldValidate
					submitButtonRenderer={(isLoading) => (
						<ModalFooter submitText="Create" isDisabled={isLoading} onCancel={onClose} />
					)}
				/>
			)}
		</>
	);
});
